import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Panel, Table, Alert, Badge, Label } from 'react-bootstrap';
import { Checkbox } from 'react-icheck';
import { Link } from 'react-router-dom';
import { ConfirmationButton } from '../../../common/components/formControls';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { renderPanelHeader, loadData } from './common';
import {
	loadAutoEnrolPreview,
	executeAutoEnrol,
	WEEKLY_PROGRAMS_AUTO_ENROL_EXECUTE_REQUEST,
	WEEKLY_PROGRAMS_AUTO_ENROL_PREVIEW_SUCCESS,
	WEEKLY_PROGRAMS_AUTO_ENROL_PREVIEW_FAILURE
} from '../actions';
import ReduxBlockUi from 'react-block-ui/redux';
import ui from '../../../common/services/ui';

const SKIP_REASON_LABELS = {
	GradeChanged: 'WeeklyProgram_AutoEnrol_Reason_GradeChanged'
};

class AutoEnrolProgram extends Component {
	constructor(props) {
		super(props);
		this.state = {
			preview: null,
			loading: true,
			loadError: null,
			selectedByTarget: {},        // { [targetId]: Set<childId> }
			confirmOverCapacity: false,
			submitting: false,
			result: null,
			submitError: null
		};
		this.handlePreviewLoaded = this.handlePreviewLoaded.bind(this);
		this.handleToggleChild = this.handleToggleChild.bind(this);
		this.handleToggleAll = this.handleToggleAll.bind(this);
		this.handleSubmit = this.handleSubmit.bind(this);
		this.handleSubmitSuccess = this.handleSubmitSuccess.bind(this);
		this.handleSubmitError = this.handleSubmitError.bind(this);
		this.handleToggleConfirmOverCapacity = this.handleToggleConfirmOverCapacity.bind(this);
	}

	componentDidMount() {
		loadData(this.props);
		const { loadAutoEnrolPreview, centerId, termId } = this.props;
		loadAutoEnrolPreview({ centerId, termId }, this.handlePreviewLoaded);
	}

	handlePreviewLoaded(response) {
		// pre-select all eligible (non-review) children per target
		const selectedByTarget = {};
		(response.classes || []).forEach(klass => {
			const set = new Set();
			(klass.candidates || []).forEach(child => {
				if (!child.needsReview) {
					set.add(child.childId);
				}
			});
			selectedByTarget[klass.targetProgramItemId] = set;
		});
		this.setState({ preview: response, loading: false, loadError: null, selectedByTarget });
	}

	getSelectedSet(targetId) {
		return this.state.selectedByTarget[targetId] || new Set();
	}

	updateSelection(targetId, mutator) {
		const current = new Set(this.getSelectedSet(targetId));
		mutator(current);
		this.setState({
			selectedByTarget: {
				...this.state.selectedByTarget,
				[targetId]: current
			}
		});
	}

	handleToggleChild(targetId, childId, checked) {
		this.updateSelection(targetId, (set) => {
			if (checked) {
				set.add(childId);
			} else {
				set.delete(childId);
			}
		});
	}

	handleToggleAll(targetId, candidates, checked) {
		this.updateSelection(targetId, (set) => {
			set.clear();
			if (checked) {
				candidates.forEach(c => set.add(c.childId));
			}
		});
	}

	handleToggleConfirmOverCapacity(_e, checked) {
		this.setState({ confirmOverCapacity: checked });
	}

	totalSelected() {
		let count = 0;
		Object.values(this.state.selectedByTarget).forEach(set => { count += set.size; });
		return count;
	}

	getCapacityOverflowTargetIds() {
		const { preview, selectedByTarget } = this.state;
		if (!preview) return [];
		return preview.classes
			.filter(klass => {
				if (klass.capacity == null) return false;
				const selectedCount = (selectedByTarget[klass.targetProgramItemId] || new Set()).size;
				const remainingSeats = Math.max(0, klass.capacity - klass.currentBookedCount);
				// Flag only when our selections exceed the seats still available — a class that is
				// already over capacity with zero selections from this round shouldn't trigger the warning.
				return selectedCount > remainingSeats;
			})
			.map(klass => klass.targetProgramItemId);
	}

	buildSelections() {
		const { preview, selectedByTarget } = this.state;
		const selections = [];
		preview.classes.forEach(klass => {
			const selected = selectedByTarget[klass.targetProgramItemId];
			if (!selected) return;
			klass.candidates.forEach(child => {
				if (selected.has(child.childId)) {
					selections.push({ targetProgramItemId: klass.targetProgramItemId, childId: child.childId });
				}
			});
		});
		return selections;
	}

	handleSubmit() {
		const { executeAutoEnrol, centerId, termId, t } = this.props;
		const selections = this.buildSelections();
		if (selections.length === 0) {
			this.setState({ submitError: t('WeeklyProgram_AutoEnrol_SelectChildrenFirst') });
			return;
		}

		this.setState({ submitting: true, submitError: null, result: null });
		executeAutoEnrol(
			{ centerId, termId, selections, confirmOverCapacity: this.state.confirmOverCapacity },
			this.handleSubmitSuccess,
			this.handleSubmitError
		);
	}

	handleSubmitSuccess(response) {
		this.setState({ submitting: false, result: response, confirmOverCapacity: false });
		const { loadAutoEnrolPreview, centerId, termId } = this.props;
		loadAutoEnrolPreview({ centerId, termId }, this.handlePreviewLoaded);
	}

	handleSubmitError(message) {
		this.setState({ submitting: false, submitError: message });
		const { loadAutoEnrolPreview, centerId, termId } = this.props;
		loadAutoEnrolPreview({ centerId, termId }, this.handlePreviewLoaded);
	}

	renderAnnualBadge(child, t) {
		return child.hasValidAnnualPayment
			? <Label bsStyle='success'>{t('WeeklyProgram_AutoEnrol_AnnualCovered')}</Label>
			: <Label bsStyle='warning'>{t('WeeklyProgram_AutoEnrol_PendingPayment')}</Label>;
	}

	renderChildAge(child) {
		return <span style={{ whiteSpace: 'nowrap' }}>{ui.formatAge(child.dateOfBirth)}</span>;
	}

	renderReasonCell(child, t) {
		if (!child.needsReview) return null;
		const key = SKIP_REASON_LABELS[child.skipReason];
		return <span className='text-danger'>{key ? t(key) : ''}</span>;
	}

	renderCapacityBadge(klass, selectedCount, t) {
		const totalAfter = selectedCount + klass.currentBookedCount;
		if (klass.capacity == null) {
			return <Badge>{t('WeeklyProgram_AutoEnrol_CapacityNoLimit', { current: totalAfter })}</Badge>;
		}
		const style = totalAfter > klass.capacity ? { backgroundColor: '#d9534f' } : { backgroundColor: '#5cb85c' };
		return <Badge style={style}>{t('WeeklyProgram_AutoEnrol_Capacity', { current: totalAfter, capacity: klass.capacity })}</Badge>;
	}

	renderClassPanel(klass, t) {
		const selected = this.getSelectedSet(klass.targetProgramItemId);
		const candidates = klass.candidates || [];
		const allSelected = candidates.length > 0 && candidates.every(c => selected.has(c.childId));

		return (
			<Panel key={klass.targetProgramItemId}>
				<Panel.Heading>
					<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
						<div>
							<strong>{klass.classLabel}</strong>
							{klass.firstLessonDate && (
								<div className='text-muted' style={{ fontSize: '0.85em' }}>
									{t('WeeklyProgram_AutoEnrol_ClassSchedule', {
										weeks: klass.numberOfWeeks,
										startDate: ui.customDateFormat(klass.firstLessonDate, 'D MMMM YYYY')
									})}
								</div>
							)}
						</div>
						{this.renderCapacityBadge(klass, selected.size, t)}
					</div>
				</Panel.Heading>
				<Panel.Body>
					{candidates.length === 0 ? (
						<em className='text-muted'>{t('WeeklyProgram_AutoEnrol_NoEligibleChildren')}</em>
					) : (
						<Table responsive hover>
							<thead>
								<tr>
									<th style={{ width: '40px' }}>
										<Checkbox
											checkboxClass='icheckbox_minimal-grey bg'
											increaseArea='20%'
											checked={allSelected}
											onChange={(e, checked) => this.handleToggleAll(klass.targetProgramItemId, candidates, checked)}
											aria-label={t('WeeklyProgram_AutoEnrol_SelectAll')}
										/>
									</th>
									<th>{t('WeeklyProgram_AutoEnrol_ColumnChild')}</th>
									<th>{t('WeeklyProgram_AutoEnrol_ColumnAge')}</th>
									<th>{t('WeeklyProgram_AutoEnrol_ColumnStatus')}</th>
									<th>{t('WeeklyProgram_AutoEnrol_ColumnReason')}</th>
								</tr>
							</thead>
							<tbody>
								{candidates.map(child => (
									<tr key={child.childId}>
										<td>
											<Checkbox
												checkboxClass='icheckbox_minimal-grey bg'
												increaseArea='20%'
												checked={selected.has(child.childId)}
												onChange={(e, checked) => this.handleToggleChild(klass.targetProgramItemId, child.childId, checked)}
											/>
										</td>
										<td><Link to={`/parents/${child.parentId}/children/${child.childId}`} >
											{child.firstName} {child.lastName}
										</Link></td>
										<td>{this.renderChildAge(child)}</td>
										<td>{this.renderAnnualBadge(child, t)}</td>
										<td>{this.renderReasonCell(child, t)}</td>
									</tr>
								))}
							</tbody>
						</Table>
					)}
				</Panel.Body>
			</Panel>
		);
	}

	renderResultAlert(t) {
		const { result } = this.state;
		if (!result) return null;
		const parts = [
			t('WeeklyProgram_AutoEnrol_SummaryConfirmed', { count: result.confirmedCount }),
			t('WeeklyProgram_AutoEnrol_SummaryPending', { count: result.pendingPaymentCount })
		];
		if (result.errors && result.errors.length > 0) {
			parts.push(t('WeeklyProgram_AutoEnrol_SummaryErrors', { count: result.errors.length }));
		}
		const bsStyle = result.errors && result.errors.length > 0 ? 'warning' : 'success';
		return (
			<Alert bsStyle={bsStyle}>
				<div>{parts.join(' · ')}</div>
				{result.errors && result.errors.length > 0 && (
					<ul className='mb-0 mt-2'>
						{result.errors.map((err, i) => (
							<li key={i}>{err.message}</li>
						))}
					</ul>
				)}
			</Alert>
		);
	}

	render() {
		const { t, center, term, isReadonly } = this.props;
		const { preview, loading, submitError, submitting, confirmOverCapacity } = this.state;
		const header = renderPanelHeader(t('WeeklyProgram_AutoEnrol_Title'), center, term);
		const backToProgramUrl = center && term ? `/weeklyProgram/action?type=edit&centerId=${center.id}&termId=${term.id}` : '/weeklyProgram';

		if (loading) {
			return (
				<Panel>
					<Panel.Heading>{header}</Panel.Heading>
					<Panel.Body>{t('WeeklyProgram_AutoEnrol_Loading')}</Panel.Body>
				</Panel>
			);
		}

		if (!preview || preview.classes.length === 0) {
			return (
				<Panel>
					<Panel.Heading>{header}</Panel.Heading>
					<Panel.Body>
						<Alert bsStyle='info'>{t('WeeklyProgram_AutoEnrol_NoSourceItems')}</Alert>
						<Link to={backToProgramUrl} className='btn btn-default'>{t('Common_BackToList')}</Link>
					</Panel.Body>
				</Panel>
			);
		}

		const overflowingTargetIds = this.getCapacityOverflowTargetIds();
		const hasOverflow = overflowingTargetIds.length > 0;
		const totalSelected = this.totalSelected();
		const submitDisabled = isReadonly || submitting || totalSelected === 0 || (hasOverflow && !confirmOverCapacity);

		return (
			<Panel>
				<Panel.Heading>{header}</Panel.Heading>
				<Panel.Body>
					<ReduxBlockUi
						block={WEEKLY_PROGRAMS_AUTO_ENROL_EXECUTE_REQUEST}
						unblock={[WEEKLY_PROGRAMS_AUTO_ENROL_PREVIEW_SUCCESS, WEEKLY_PROGRAMS_AUTO_ENROL_PREVIEW_FAILURE]}
					>
						{this.renderResultAlert(t)}
						{submitError && <Alert bsStyle='danger'>{submitError}</Alert>}

						{preview.classes.map(klass => this.renderClassPanel(klass, t))}

						{hasOverflow && (
							<Alert bsStyle='warning'>
								<div>{t('WeeklyProgram_AutoEnrol_OverCapacityWarning')}</div>
								<label style={{ display: 'flex', alignItems: 'center', marginTop: '0.5em', fontWeight: 'normal' }}>
									<Checkbox
										checkboxClass='icheckbox_minimal-grey bg'
										increaseArea='20%'
										checked={confirmOverCapacity}
										onChange={this.handleToggleConfirmOverCapacity}
									/>
									<span style={{ marginLeft: '0.5em' }}>{t('WeeklyProgram_AutoEnrol_ConfirmOverCapacity')}</span>
								</label>
							</Alert>
						)}

						<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1em' }}>
							<Link to={backToProgramUrl} className='btn btn-default'>{t('Common_BackToList')}</Link>
							<ConfirmationButton
								onConfirmation={this.handleSubmit}
								confirmationTitle={t('WeeklyProgram_AutoEnrol_ConfirmTitle')}
								confirmationMessage={<div dangerouslySetInnerHTML={{ __html: t('WeeklyProgram_AutoEnrol_ConfirmMessage') }} />}
								displayType={'inline'}
								label={`${t('WeeklyProgram_AutoEnrol_Submit')} (${totalSelected})`}
								bsStyle='primary'
								icon={faCheck}
								disabled={submitDisabled}
							/>
						</div>
					</ReduxBlockUi>
				</Panel.Body>
			</Panel>
		);
	}
}

const mapDispatchToProps = {
	loadAutoEnrolPreview,
	executeAutoEnrol
};

export default connect(null, mapDispatchToProps)(AutoEnrolProgram);
